import React, { useState } from 'react';
import { CreditCard, Trash2, Star, StarOff, Plus } from 'lucide-react';

const cardBrandIcons = {
  visa: 'VISA',
  mastercard: 'MC',
  amex: 'AMEX',
  discover: 'DISC',
};

const PaymentMethodsList = ({
  paymentMethods = [],
  onAddNew,
  onRemove,
  onSetDefault,
  isAdmin = false,
  loading = false,
}) => {
  const [removingId, setRemovingId] = useState(null);
  const [settingDefaultId, setSettingDefaultId] = useState(null);

  const handleRemove = async (id) => {
    if (!window.confirm('Remove this payment method?')) return;
    setRemovingId(id);
    try {
      await onRemove(id);
    } finally {
      setRemovingId(null);
    }
  };

  const handleSetDefault = async (id) => {
    setSettingDefaultId(id);
    try {
      await onSetDefault(id);
    } finally {
      setSettingDefaultId(null);
    }
  };

  if (!isAdmin) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
        <h3 className="text-lg font-semibold text-freshroad-black mb-4 flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-freshroad-teal" />
          Payment Methods
        </h3>
        <p className="text-gray-500 text-sm">
          Only the group owner can manage payment methods.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-freshroad-black flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-freshroad-teal" />
          Payment Methods
        </h3>
        <button
          onClick={onAddNew}
          disabled={loading}
          className="flex items-center gap-1 px-3 py-1.5 bg-freshroad-teal text-white rounded-lg hover:bg-freshroad-teal-dark transition-colors text-sm font-medium disabled:opacity-50"
        >
          <Plus className="w-4 h-4" />
          Add Card
        </button>
      </div>

      {paymentMethods.length === 0 ? (
        <p className="text-gray-500 text-sm">No payment methods added yet.</p>
      ) : (
        <div className="space-y-3">
          {paymentMethods.map((pm) => (
            <div
              key={pm.id}
              className={`flex items-center justify-between p-3 rounded-lg border ${
                pm.isDefault ? 'border-freshroad-teal bg-freshroad-teal/5' : 'border-gray-200'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-8 bg-gray-100 rounded flex items-center justify-center text-xs font-bold text-gray-600">
                  {cardBrandIcons[pm.brand?.toLowerCase()] || pm.brand?.toUpperCase() || 'CARD'}
                </div>
                <div>
                  <p className="font-medium text-gray-900">
                    •••• {pm.last4}
                  </p>
                  <p className="text-sm text-gray-500">
                    Expires {pm.expiryMonth}/{pm.expiryYear}
                  </p>
                </div>
                {pm.isDefault && (
                  <span className="flex items-center gap-1 text-xs text-freshroad-teal bg-freshroad-teal/10 px-2 py-0.5 rounded">
                    <Star className="w-3 h-3" />
                    Default
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {!pm.isDefault && (
                  <button
                    onClick={() => handleSetDefault(pm.id)}
                    disabled={settingDefaultId === pm.id}
                    className="p-2 text-gray-400 hover:text-freshroad-teal transition-colors disabled:opacity-50"
                    title="Set as default"
                  >
                    <StarOff className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={() => handleRemove(pm.id)}
                  disabled={removingId === pm.id}
                  className="p-2 text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50"
                  title="Remove"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PaymentMethodsList;
